/**
 * Phase 20 / Task 20.7 — §80 step 1, upload.
 *
 * The file's real problems (an `.xls`, a corrupt workbook, a password-protected
 * one) are diagnosed by the API from the bytes and come back on a 200 as a
 * FAILED batch with a sentence for the hauler (DEC-255). This screen shows that
 * sentence verbatim and does not second-guess the extension — the only check
 * made here is the 50 MB limit, so a 2 GB drop is refused before it uploads.
 *
 * ── The per-sheet entity selector (§80: "Import as: [ Operators ▾ ]") ──────
 * Every staged sheet can be pointed at a different template, or skipped, with
 * `PATCH /v1/imports/:id/sheets` (migration 0111). A sheet the detector could
 * not place is staged as UNASSIGNED (`needsTemplate`) rather than dropped, and
 * Continue stays disabled until each one has a template or is skipped — the
 * commit refuses a batch that still holds one, and three steps later is the
 * worse place to find that out.
 *
 * Skipping DELETES the sheet's staged rows (that route's header says why), so it
 * is confirmed first and cannot be taken back on this batch: the way back is to
 * upload the file again, and the screen says so.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { MAX_UPLOAD_BYTES, updateSheets, uploadImportFile } from './api';
import { familyLabel } from './mapping-state';
import { useTemplateCatalog, type TemplateSummary } from './templates-query';
import type { SheetDecision, SheetsUpdate, UploadResult, UploadedSheet } from './types';
import { exampleRowSentence } from './example-row';
import { ActionButton, Notice, Panel, TONE, muted, plural, selectStyle } from './ui';

const SKIP = '__skip__';

export const SKIPPED_NOTE =
  'Skipped — this sheet will not be imported. To import it, upload the file again.';

function confidenceText(sheet: UploadedSheet): string {
  if (sheet.needsTemplate === true) {
    return 'We could not tell what this sheet holds. Pick the matching template, or skip the sheet.';
  }
  switch (sheet.confidence) {
    case 'EXACT':
      return 'Matched by sheet name';
    case 'SUGGESTED':
      return 'Suggested match — check the columns on the next step';
    case 'UNKNOWN':
      return 'Not recognised — this sheet will be skipped';
  }
}

/**
 * The upload result as it stands after a sheet decision. The PATCH answers with
 * every sheet still staged; a sheet missing from it was skipped.
 */
export function applySheetsUpdate(
  uploaded: UploadResult,
  decision: SheetDecision,
  res: SheetsUpdate,
): UploadResult {
  const sheets = uploaded.sheets.map((s): UploadedSheet => {
    const now = res.sheets.find((r) => (r.sheetName ?? '') === s.sheetName);
    if (now !== undefined) {
      const chosen = s.sheetName === decision.sheetName;
      return {
        ...s,
        staged: true,
        entityFamily: now.entityFamily,
        needsTemplate: now.needsTemplate,
        rowCount: now.rowCount,
        ...(chosen ? { note: 'You chose this template.' } : {}),
      };
    }
    if (!s.staged) return s;
    return { ...s, staged: false, entityFamily: null, needsTemplate: false, note: SKIPPED_NOTE };
  });
  const totalRows = res.sheets.reduce((n, r) => (r.needsTemplate ? n : n + r.rowCount), 0);
  return { ...uploaded, sheets, totalRows };
}

function SheetRow(props: {
  readonly sheet: UploadedSheet;
  readonly templates: readonly TemplateSummary[];
  readonly busy: boolean;
  readonly onChoose: (entityFamily: string | null) => void;
}): React.JSX.Element {
  const { sheet, templates } = props;
  const label = (key: string): string =>
    templates.find((t) => t.key === key)?.sheetName ?? familyLabel(key);
  const skippedByHauler = !sheet.staged && sheet.note === SKIPPED_NOTE;
  const tone =
    sheet.problem !== undefined || (!sheet.staged && !skippedByHauler)
      ? TONE.error
      : sheet.needsTemplate === true || !sheet.staged || sheet.confidence !== 'EXACT'
        ? TONE.warn
        : TONE.ok;
  const name = sheet.sheetName === '' ? 'This file' : `Sheet "${sheet.sheetName}"`;
  const selectId = `import-sheet-${sheet.sheetName === '' ? '_file' : sheet.sheetName}`;
  const suggested = (sheet.alternatives ?? []).filter((k) => templates.some((t) => t.key === k));

  return (
    <li
      style={{
        listStyle: 'none',
        padding: '12px 16px',
        borderLeft: `4px solid ${tone.border}`,
        backgroundColor: tone.bg,
        borderRadius: '6px',
      }}
    >
      {sheet.staged ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label htmlFor={selectId} style={{ fontSize: 16 }}>
            {name} — Import as:
          </label>
          <select
            id={selectId}
            disabled={props.busy || templates.length === 0}
            value={sheet.needsTemplate === true ? '' : (sheet.entityFamily ?? '')}
            onChange={(e): void => props.onChoose(e.target.value === SKIP ? null : e.target.value)}
            style={{ ...selectStyle, minWidth: 240 }}
          >
            {sheet.needsTemplate === true && (
              <option value="" disabled>
                Choose a template…
              </option>
            )}
            {suggested.length > 0 && (
              <optgroup label="Suggested">
                {suggested.map((k) => (
                  <option key={`s-${k}`} value={k}>
                    {label(k)}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="All templates">
              {templates.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.sheetName}
                </option>
              ))}
            </optgroup>
            <option value={SKIP}>Skip this sheet</option>
          </select>
          <span style={muted}>({plural(sheet.rowCount, 'row')})</span>
        </div>
      ) : (
        <div style={{ fontSize: '16px' }}>
          {name} — <strong>Skipped</strong>
          <span style={{ ...muted, marginLeft: '8px' }}>({plural(sheet.rowCount, 'row')})</span>
        </div>
      )}
      <div style={{ ...muted, marginTop: 4 }}>
        {/* `note` is the API's sentence; `problem` is a code (NO_DATA_ROWS) and
            is shown only if no sentence came with it. Found on dev, 20.7 E2E. */}
        {sheet.note ?? sheet.problem ?? confidenceText(sheet)}
      </div>
      {/* DEC-285: the row the API dropped as the template's example, with its
          content, so the hauler can see it was not their data. */}
      {sheet.exampleRowSkipped != null && (
        <div
          data-testid="import-example-row-skipped"
          style={{ fontSize: 14, marginTop: 4, overflowWrap: 'anywhere' }}
        >
          {exampleRowSentence(sheet.exampleRowSkipped)}
        </div>
      )}
    </li>
  );
}

export function UploadStep(props: {
  readonly onUploaded: (result: UploadResult) => void;
  readonly uploaded: UploadResult | null;
  readonly onContinue: () => void;
  readonly onReset: () => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  const templates = useTemplateCatalog();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);

  const upload = useMutation<UploadResult, Error, File>({
    mutationFn: (file) => uploadImportFile(apiOpts, file),
    onSuccess: (result) => props.onUploaded(result),
  });

  const accept = (file: File | undefined): void => {
    if (file === undefined) return;
    setLocalError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setLocalError(
        'That file is over the 50 MB limit. Split it into smaller files and try again.',
      );
      return;
    }
    upload.mutate(file);
  };

  const choose = useMutation<
    UploadResult,
    Error,
    { uploaded: UploadResult; decision: SheetDecision }
  >({
    mutationFn: async ({ uploaded: u, decision }) =>
      applySheetsUpdate(u, decision, await updateSheets(apiOpts, u.batchId, [decision])),
    onSuccess: (next) => {
      // The mapping and preview are keyed by family; neither may outlive a change.
      qc.removeQueries({ queryKey: ['imports', next.batchId] });
      props.onUploaded(next);
    },
  });

  const errorText = localError ?? upload.error?.message ?? null;
  const { uploaded } = props;
  if (uploaded !== null) {
    const stagedSheets = uploaded.sheets.filter((s) => s.staged);
    const undecided = stagedSheets.filter((s) => s.needsTemplate === true);
    const onChoose = (sheet: UploadedSheet, entityFamily: string | null): void => {
      if (entityFamily === null) {
        const rows = plural(sheet.rowCount, 'row');
        const what = sheet.sheetName === '' ? 'this file' : `the sheet "${sheet.sheetName}"`;
        if (
          !window.confirm(
            `Skip ${what}? Its ${rows} will not be imported. To import it later, upload the file again.`,
          )
        ) {
          return;
        }
      }
      choose.mutate({ uploaded, decision: { sheetName: sheet.sheetName, entityFamily } });
    };
    return (
      <Panel labelledBy="import-upload-heading">
        <h2 id="import-upload-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
          Step 1 of 4 — File checked
        </h2>
        {uploaded.status === 'FAILED' ? (
          <div style={{ marginTop: '16px' }}>
            <Notice tone="error" role="alert">
              {uploaded.message ?? 'We could not read that file.'}
            </Notice>
          </div>
        ) : (
          <>
            <p style={{ ...muted, margin: '8px 0 16px' }}>
              {plural(uploaded.totalRows, 'row')} found. Each sheet is matched to what it holds.
            </p>
            {(uploaded.notes ?? []).map((n) => (
              <div key={n} style={{ marginBottom: '12px' }}>
                <Notice tone="warn">{n}</Notice>
              </div>
            ))}
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: 0 }}>
              {uploaded.sheets.map((s) => (
                <SheetRow
                  key={s.sheetName}
                  sheet={s}
                  templates={templates.data ?? []}
                  busy={choose.isPending}
                  onChoose={(family): void => onChoose(s, family)}
                />
              ))}
            </ul>
            {choose.isError && (
              <div style={{ marginTop: '12px' }}>
                <Notice tone="error" role="alert">
                  {choose.error.message}
                </Notice>
              </div>
            )}
            {templates.isError && (
              <div style={{ marginTop: '12px' }}>
                <Notice tone="error" role="alert">
                  The list of templates could not be loaded, so sheets cannot be changed right now.{' '}
                  {templates.error.message}
                </Notice>
              </div>
            )}
            {undecided.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <Notice tone="warn" role="status">
                  {undecided.length === 1
                    ? 'One sheet needs a template.'
                    : `${undecided.length.toString()} sheets need a template.`}{' '}
                  Choose what each one holds, or skip it, before you continue.
                </Notice>
              </div>
            )}
            {stagedSheets.length === 0 && (
              <div style={{ marginTop: '16px' }}>
                <Notice tone="error" role="alert">
                  No sheet in this file matched something we can import. Rename each sheet to the
                  template’s sheet name (for example “Customers” or “Trucks”), or start from a
                  template under Templates.
                </Notice>
              </div>
            )}
          </>
        )}
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
          {uploaded.status !== 'FAILED' && stagedSheets.length > 0 && (
            <ActionButton
              variant="primary"
              disabled={undecided.length > 0 || choose.isPending}
              onClick={props.onContinue}
            >
              Continue to column mapping
            </ActionButton>
          )}
          <ActionButton onClick={props.onReset}>Choose a different file</ActionButton>
        </div>
      </Panel>
    );
  }

  return (
    <Panel labelledBy="import-upload-heading">
      <h2 id="import-upload-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
        Step 1 of 4 — Upload a file
      </h2>
      <p style={{ ...muted, margin: '8px 0 16px' }}>
        Excel (.xlsx) or CSV, up to 50 MB. Nothing is saved until you confirm the import on the last
        step.
      </p>
      <div
        data-testid="import-drop-zone"
        onDragOver={(e): void => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(): void => setDragOver(false)}
        onDrop={(e): void => {
          e.preventDefault();
          setDragOver(false);
          accept(e.dataTransfer.files[0]);
        }}
        style={{
          border: `2px dashed ${dragOver ? 'var(--color-accent-blue)' : 'var(--color-border-secondary)'}`,
          borderRadius: '8px',
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: dragOver ? 'var(--color-bg-tertiary)' : 'transparent',
        }}
      >
        <p style={{ fontSize: '16px', marginBottom: '16px' }}>Drag your file here, or</p>
        <ActionButton
          variant="primary"
          disabled={upload.isPending}
          onClick={(): void => inputRef.current?.click()}
        >
          {upload.isPending ? 'Checking the file…' : 'Choose a file'}
        </ActionButton>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.csv"
          aria-label="Import file"
          style={{ display: 'none' }}
          onChange={(e): void => {
            accept(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      {errorText !== null && (
        <div style={{ marginTop: '16px' }}>
          <Notice tone="error" role="alert">
            {errorText}
          </Notice>
        </div>
      )}
    </Panel>
  );
}
